import React, { useEffect, useState } from "react";
import { Flag, Utensils, Timer, Zap, HeartPulse, CheckCircle2, Circle, Droplets, Package, Beaker, Repeat } from "lucide-react";

// Shared race-day fuelling blocks used by both the subscriber Race Hub and the
// one-off Race Pack plan, so both read from identical logic.

export const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function parseLocal(iso) {
  const [y, m, d] = (iso || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function addDaysISO(iso, n) {
  const d = parseLocal(iso); d.setDate(d.getDate() + n);
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function timeSubtractStr(hhmm, hoursBack) {
  const [h, m] = String(hhmm || "08:00").split(":").map(Number);
  let mins = (h || 0) * 60 + (m || 0) - hoursBack * 60;
  while (mins < 0) mins += 1440;
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

export function timeAddMins(hhmm, addMins) {
  const [h, m] = String(hhmm || "08:00").split(":").map(Number);
  const mins = ((h || 0) * 60 + (m || 0) + addMins) % 1440;
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

export const fmtMin = (min) => {
  const h = Math.floor((min || 0) / 60), m = (min || 0) % 60;
  return h ? `${h}h ${m ? `${m}m` : ""}`.trim() : `${m}m`;
};

const round50 = (n) => Math.round(n / 50) * 50;

// Carbs/hr planning window from race duration. These come DOWN as the day gets
// longer: gut tolerance, not glycogen, is the limiter, and nobody absorbs
// 90 g/hr for eight hours. We plan at the MIDDLE of the window, never the top.
export function carbWindow(durMin) {
  if (durMin >= 600) return [40, 60];    // 10 h+ — real food, individual
  if (durMin >= 360) return [50, 70];    // 6-10 h
  if (durMin >= 240) return [60, 80];    // 4-6 h
  if (durMin >= 150) return [60, 90];    // 2.5-4 h (marathon)
  if (durMin >= 75) return [30, 60];     // 1h15-2h30 (half marathon)
  if (durMin >= 45) return [0, 30];      // 45-75 min — only for hard efforts
  return [0, 0];                         // under 45 min — nothing needed
}

// Gut tolerance scales with body size, not just duration. ~1.2 g/kg/hr is the
// realistic ceiling for a practised athlete; 55 kg tops out near 65 g/hr.
export const CARB_GKG_PER_HR = 1.2;
export const weightCarbCap = (weightKg) =>
  weightKg ? Math.round((weightKg * CARB_GKG_PER_HR) / 5) * 5 : 0;

// Feed sources. Gels are one option, never the compulsory method.
export const FUEL_SOURCES = {
  gel: { key: "gel", label: "gel", carbs: 30, unit: "1 × 30 g gel" },
  drink: { key: "drink", label: "carb drink", carbs: 30, unit: "30 g carb mix (bottle)" },
  chews: { key: "chews", label: "chews", carbs: 30, unit: "30 g chews" },
  food: { key: "food", label: "real food", carbs: 30, unit: "30 g real food" },
};

// Defaults used only when the athlete hasn't told us their limits — the plan
// is labelled provisional whenever these kick in.
export const DEFAULT_GELS_PER_HR = 2;
export const DEFAULT_GELS_TOTAL = 10;

// Hard physiological ceilings. Nothing in the app may advise beyond these.
export const MAX_CARBS_PER_HR = 120;     // multiple transportable carbs, trained gut
export const MAX_SODIUM_PER_HR = 1500;   // heavy salty sweater, hot conditions
export const MAX_FLUID_PER_HR = 1000;    // above this you risk hyponatraemia
export const MAX_GELS = 12;              // beyond this, switch to food + drink mix
export const MAX_TABS = 10;              // beyond this, use concentrated sachets
export const GEL_CARBS = 30;             // TORQ, PF&H and most brands we recommend
export const CHEW_CARBS = 30;            // typical chew pack

// Pre-race carbs scale with how long you'll be out there — a 1-hour race does
// not need a 2 g/kg breakfast.
export function preCarbCeilingGkg(durMin) {
  if (durMin >= 240) return 2.0;
  if (durMin >= 150) return 1.5;
  if (durMin >= 90) return 1.2;
  return 1.0;
}
export const MAX_POST_CARB_GKG = 1.2;    // immediate post-race ceiling, g per kg

export const capPreCarbs = (g, weightKg, durMin = 180) =>
  weightKg
    ? Math.min(Math.round(g || 0), Math.round(weightKg * preCarbCeilingGkg(durMin)))
    : Math.round(g || 0);
export const capPostCarbs = (g, weightKg) =>
  weightKg ? Math.min(Math.round(g || 0), Math.round(weightKg * MAX_POST_CARB_GKG)) : Math.round(g || 0);

// Hydration is planned on its own — never raised to carry more carbs. Roughly
// 8 ml/kg/hr in mild conditions, 10.5 in the heat, so a 55 kg athlete drinks
// ~450 ml/hr rather than the 700 ml/hr a 90 kg athlete needs.
function fluidCeiling(durMin, heat, weightKg) {
  if (!durMin) return 0;
  const perKg = heat ? 10.5 : 8;
  const base = weightKg ? Math.round((weightKg * perKg) / 50) * 50 : (heat ? 700 : 550);
  const floor = durMin < 90 ? 350 : 400;
  const cap = Math.min(heat ? 900 : 700, MAX_FLUID_PER_HR);
  return Math.max(floor, Math.min(base, cap));
}
// Sodium follows sweat losses, not carb totals. 400-600 mg/hr covers most
// athletes; heat and all-day efforts push it up, nothing goes near 1 g/hr.
function sodiumCeiling(durMin, heat) {
  if (!durMin) return 0;
  if (durMin < 90) return heat ? 600 : 400;
  if (durMin < 240) return heat ? 700 : 500;
  return Math.min(heat ? 800 : 600, MAX_SODIUM_PER_HR);
}

// Single source of truth for the race-day numbers every card renders from.
//
// Three figures are kept deliberately separate, per coaching protocol:
//   suggestedPerHr — what the duration and bodyweight justify
//   practisedPerHr — what the athlete has actually sustained in training
//   cph            — what we PLAN, which never exceeds practised tolerance
//                    unless the plan is flagged as something to rehearse.
export function raceNumbers({
  durMin = 0, weightKg = 0, carbsPerHour, heat = false, sodiumPerHr, fuelling = {}, sport = "",
}) {
  const [recMin, recMax] = carbWindow(durMin);
  const wCap = weightCarbCap(weightKg);
  const hours = durMin / 60;

  // Planning target: middle of the window, then capped by bodyweight.
  const mid = Math.round((recMin + recMax) / 2 / 5) * 5;
  const allowedMax = Math.min(recMax, wCap || recMax, MAX_CARBS_PER_HR);
  const suggestedPerHr = Math.min(mid, allowedMax);

  const practisedPerHr = Number(fuelling.practisedPerHr) || 0;
  const practisedHours = Number(fuelling.practisedHours) || 0;
  const requested = Number(carbsPerHour) || 0;

  // An athlete may go above the window only on demonstrated tolerance.
  const headroom = Math.max(allowedMax, Math.min(practisedPerHr, MAX_CARBS_PER_HR));
  const target = requested || suggestedPerHr;
  let aim = Math.max(recMin ? Math.min(recMin, target) : 0, Math.min(target, headroom));
  // Tolerance proven over 2 h says nothing about hour eight — above a practised
  // rate we plan at the practised figure instead.
  const unpractised = practisedPerHr > 0 && aim > practisedPerHr;
  if (unpractised) aim = practisedPerHr;
  const underpractised = practisedPerHr > 0 && practisedHours > 0 && hours > practisedHours * 2;

  // The plan is delivered as 30 g feeds on a round interval, so the rate we
  // QUOTE is the rate those feeds actually deliver — never the raw aim.
  const interval = feedInterval(aim);
  const cph = interval ? Math.round((GEL_CARBS * 60) / interval) : 0;

  const fluidPerHr = fluidCeiling(durMin, heat, weightKg);
  const naCeiling = sodiumCeiling(durMin, heat);
  const requestedNa = Number(sodiumPerHr) || naCeiling;
  const naPerHr = Math.min(requestedNa, naCeiling);

  // Which sources this athlete will actually take. Nobody chews a rice cake in
  // a 10 k, half or marathon — on foot, real food only appears at ultra length.
  const src = fuelling.sources || {};
  const isRun = /run|marathon|half|10k|5k/i.test(String(sport));
  const foodPossible = isRun ? durMin >= 300 : durMin >= 150;
  const sources = {
    gel: src.gel !== false,
    drink: src.drink !== false,
    chews: src.chews !== false,
    food: foodPossible && (src.food === undefined ? true : !!src.food),
  };
  // Gel limits: the athlete's own if given, otherwise conservative defaults
  // (which make the plan provisional).
  const gelsPerHrLimit = Number(fuelling.maxGelsPerHour) || DEFAULT_GELS_PER_HR;
  const gelsTotalStated = Number(fuelling.maxGelsTotal) || 0;
  const gelLimit = Math.min(
    gelsTotalStated || DEFAULT_GELS_TOTAL,
    Math.floor(gelsPerHrLimit * Math.max(hours, 0)) || 0,
  );
  const provisional = !practisedPerHr || !gelsTotalStated;

  const feeds = interval && durMin ? Math.floor((durMin - 10) / interval) : 0;

  const base = {
    recMin, recMax, aim, requested, suggestedPerHr, practisedPerHr, practisedHours,
    ceiling: allowedMax, weightCap: wCap,
    cphCapped: requested > allowedMax,
    unpractised, underpractised, provisional,
    sources, gelsPerHrLimit, gelLimit, gelsTotalStated, isRun,
    noSources: !sources.gel && !sources.drink && !sources.chews && !sources.food,
    slowerFinishMin: Number(fuelling.slowerFinishMin) || 0,
    fluidPerHr, naPerHr, naCeiling,
    naCapped: requestedNa > naCeiling,
    durMin, weightKg, heat,
    shortRace: durMin > 0 && durMin < 75,
    longRace: durMin >= 360,
    feedInterval: interval, feeds,
    fluidTotal: Math.round(fluidPerHr * hours),
    naTotal: Math.round(naPerHr * hours),
  };
  // Quote the rate the schedule ACTUALLY delivers — after gel limits and
  // ruled-out sources have had their say — so the headline g/hr, the timetable
  // and the finish-line total can never disagree.
  const sch = feedSchedule(base, "08:00");
  const carbTotal = sch.cumC;
  const quoted = carbTotal && hours ? Math.round((carbTotal / hours) / 5) * 5 : 0;

  return {
    ...base,
    cph: quoted,
    carbTotal,
    unfilled: sch.unfilled,
    shortfallPerHr: Math.max(0, suggestedPerHr - quoted),
  };
}

export function SectionCard({ icon: Icon, color = "#E2E30A", title, sub, children, testid }) {
  return (
    <section className="border border-white/10 bg-[#141414] break-inside-avoid" data-testid={testid}>
      <div className="flex items-start gap-3 border-b border-white/5 px-4 py-3">
        <div className="grid place-items-center h-9 w-9 shrink-0"
          style={{ background: `${color}22`, border: `1px solid ${color}55` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color }}>{sub}</p>
          <p className="text-sm font-display uppercase text-white tracking-tight leading-tight">{title}</p>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Bullet({ children, testid }) {
  return (
    <li className="flex items-start gap-2 text-[12px] text-zinc-200 leading-snug" data-testid={testid}>
      <span className="mt-1.5 h-1 w-1 bg-[#E2E30A] shrink-0" />
      <span>{children}</span>
    </li>
  );
}

// ---------------------------------------------------------------- countdown
export function CountdownBlock({ raceIso, startTime = "08:00", name, distance, durationMin }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!raceIso) return null;
  const [h, m] = String(startTime).split(":").map(Number);
  const target = parseLocal(raceIso);
  target.setHours(h || 8, m || 0, 0, 0);
  const diff = target.getTime() - now;
  const abs = Math.abs(diff);
  const cd = {
    done: diff <= 0,
    days: Math.floor(abs / 86400000),
    hours: Math.floor((abs % 86400000) / 3600000),
    mins: Math.floor((abs % 3600000) / 60000),
    secs: Math.floor((abs % 60000) / 1000),
  };
  const nice = parseLocal(raceIso).toLocaleDateString(undefined,
    { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="border border-[#FF3B30]/40 bg-gradient-to-br from-[#FF3B30]/10 via-transparent to-transparent p-5"
      data-testid="race-countdown">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#FF3B30] font-bold">Race</p>
          <p className="font-display text-2xl sm:text-3xl text-white tracking-tight leading-tight">{name || "Your Race"}</p>
          <p className="text-xs text-zinc-400 mt-0.5">{nice} · Start {startTime}</p>
        </div>
        <div className="flex gap-2 text-[10px] uppercase tracking-wider">
          {distance ? <span className="border border-white/15 px-2 py-1 text-zinc-200">{distance}</span> : null}
          {durationMin ? <span className="border border-white/15 px-2 py-1 text-zinc-200">Est {fmtMin(durationMin)}</span> : null}
        </div>
      </div>
      <p className="text-[9px] uppercase tracking-[0.24em] font-bold text-zinc-400 mb-2">
        {cd.done ? "Since the gun" : "Until the gun"}
      </p>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[["Days", cd.days], ["Hrs", cd.hours], ["Min", cd.mins], ["Sec", cd.secs]].map(([lbl, val]) => (
          <div key={lbl} className="border border-white/10 bg-black/40 py-2.5">
            <p className="font-display text-3xl sm:text-4xl text-[#FF3B30] leading-none tabular-nums">
              {String(val).padStart(2, "0")}
            </p>
            <p className="text-[9px] uppercase tracking-widest text-zinc-400 mt-1">{lbl}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------- fuelling timetable
// Every 20 minutes from the gun: what to take, and the running total.
// Real feed events, not arbitrary 20-minute fractions. Gels are 30 g, so the
// interval falls out of the carbs/hr target: 60 g/hr = a gel every 30 min,
// 90 g/hr = every 20 min, 120 g/hr = every 15 min, 30 g/hr = every hour.
export function feedInterval(cph) {
  if (!cph) return 0;
  const raw = (GEL_CARBS / cph) * 60;              // minutes between 30 g feeds
  return Math.max(10, Math.min(60, Math.round(raw / 5) * 5));
}

export function feedSchedule(n, startTime = "08:00") {
  const interval = n.feedInterval || feedInterval(n.cph);
  if (!interval || !n.durMin) return { interval, rows: [], cumC: 0, cumF: 0, cumNa: 0, counts: {} };
  const rows = [];
  const counts = { gel: 0, drink: 0, chews: 0, food: 0 };
  let cumC = 0, cumF = 0, cumNa = 0, prev = 0, unfilled = 0;
  let gelsThisHour = 0, lastHour = 0;
  // Rotation order. Gels are rationed against the athlete's hourly and total
  // limits; everything else fills the gaps, with savoury real food taking over
  // the long middle hours when nobody wants another sweet gel.
  const order = ["gel", "drink", "chews", "food"];
  // No point taking a feed in the closing minutes — it won't be absorbed.
  for (let t = interval; t <= n.durMin - 10; t += interval) {
    prev = t;
    const hourIdx = Math.floor((t - 1) / 60);
    if (hourIdx !== lastHour) { lastHour = hourIdx; gelsThisHour = 0; }
    const lateRace = n.durMin >= 240 && t > 240;

    let pick = null;
    const gelAllowed = n.sources.gel && counts.gel < n.gelLimit
      && gelsThisHour < (lateRace ? 1 : n.gelsPerHrLimit);
    const rotation = lateRace ? ["food", "drink", "chews", "gel"] : order;
    for (let k = 0; k < rotation.length && !pick; k += 1) {
      const key = rotation[(rows.length + k) % rotation.length];
      if (!n.sources[key]) continue;
      if (key === "gel" && !gelAllowed) continue;
      pick = key;
    }
    if (!pick) {
      // Nothing left that the athlete will take — record the carb gap, but
      // keep drinking: fluid and sodium still have to go in.
      unfilled += 1;
      const dryF = round50((n.fluidPerHr * t) / 60);
      const dryNa = Math.round((n.naPerHr * t) / 60 / 10) * 10;
      rows.push({
        t, clock: timeAddMins(startTime, t), c: 0, source: "fluid",
        item: "Fluid + electrolyte only", f: dryF - cumF, na: dryNa - cumNa,
        cumC, cumF: dryF, cumNa: dryNa,
      });
      cumF = dryF; cumNa = dryNa;
      continue;
    }
    if (pick === "gel") gelsThisHour += 1;
    counts[pick] += 1;

    // Fluid and sodium run on an exact cumulative total and are only rounded
    // for display, so per-row rounding can't inflate the race totals.
    const newF = round50((n.fluidPerHr * t) / 60);
    const newNa = Math.round((n.naPerHr * t) / 60 / 10) * 10;
    const f = newF - cumF;
    const na = newNa - cumNa;
    cumC += GEL_CARBS; cumF = newF; cumNa = newNa;
    rows.push({
      t, clock: timeAddMins(startTime, t), c: GEL_CARBS, source: pick,
      item: FUEL_SOURCES[pick].unit, f, na, cumC, cumF, cumNa,
    });
  }
  // The run-in after the last feed still costs fluid and sodium — fold it into
  // the final row so the timetable, what-to-carry and electrolytes agree.
  if (rows.length && n.durMin > prev) {
    const endF = round50((n.fluidPerHr * n.durMin) / 60);
    const endNa = Math.round((n.naPerHr * n.durMin) / 60 / 10) * 10;
    const last = rows[rows.length - 1];
    last.f += endF - cumF; last.na += endNa - cumNa;
    cumF = endF; cumNa = endNa;
    last.cumF = cumF; last.cumNa = cumNa;
  }
  return { interval, rows, cumC, cumF, cumNa, counts, unfilled };
}

export function FuellingTimetable({ n, startTime = "08:00" }) {
  if (!n?.durMin) return null;
  const { interval, rows, cumC, cumF, cumNa, counts, unfilled } = feedSchedule(n, startTime);
  if (!rows.length) {
    return (
      <SectionCard icon={Timer} color="#E2E30A" title="Fuelling Timetable"
        sub={fmtMin(n.durMin)} testid="race-timetable">
        <p className="text-[12px] text-zinc-300">
          A race this short needs no solid fuel — a sports drink or a carb mouth-rinse is enough.
        </p>
      </SectionCard>
    );
  }
  const mix = Object.entries(counts).filter(([, v]) => v > 0)
    .map(([k, v]) => `${v} × ${FUEL_SOURCES[k].label}`).join(" · ");
  return (
    <SectionCard icon={Timer} color="#E2E30A" title="Fuelling Timetable"
      sub={`${n.cph} g/hr · a feed every ${interval} min · ${fmtMin(n.durMin)}`} testid="race-timetable">
      <div className="grid grid-cols-3 gap-2 mb-3" data-testid="timetable-three-numbers">
        <div className="border border-white/10 bg-black/30 p-2.5">
          <p className="text-[9px] uppercase tracking-widest text-zinc-400">Suggested</p>
          <p className="font-display text-xl text-white leading-none tabular-nums mt-1">{n.suggestedPerHr}<span className="text-[10px] text-zinc-400 ml-1">g/hr</span></p>
        </div>
        <div className="border border-white/10 bg-black/30 p-2.5">
          <p className="text-[9px] uppercase tracking-widest text-zinc-400">Practised</p>
          <p className="font-display text-xl leading-none tabular-nums mt-1"
            style={{ color: n.practisedPerHr ? "#fff" : "#71717A" }}>
            {n.practisedPerHr ? <>{n.practisedPerHr}<span className="text-[10px] text-zinc-400 ml-1">g/hr</span></> : "—"}
          </p>
        </div>
        <div className="border border-[#E2E30A]/50 bg-[#E2E30A]/[0.07] p-2.5">
          <p className="text-[9px] uppercase tracking-widest text-[#E2E30A]">Planned</p>
          <p className="font-display text-xl text-white leading-none tabular-nums mt-1">{n.cph}<span className="text-[10px] text-zinc-400 ml-1">g/hr</span></p>
        </div>
      </div>

      {n.provisional && (
        <div className="border border-[#FF9500]/50 bg-[#FF9500]/[0.07] p-3 mb-3" data-testid="timetable-provisional">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF9500] font-bold">Provisional plan</p>
          <p className="text-[12px] text-zinc-100 mt-1.5 leading-snug">
            We don't yet know what you've actually practised
            {!n.gelsTotalStated && <> or how many gels you'll willingly take</>}. We've planned
            conservatively and assumed no more than {n.gelsPerHrLimit} gels an hour. Add your
            practised intake and gel limits to the race setup and this becomes a race-ready plan.
          </p>
        </div>
      )}
      {n.unpractised && n.suggestedPerHr > n.practisedPerHr && (
        <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/[0.07] p-3 mb-3" data-testid="timetable-unpractised">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] font-bold">Capped to what you've practised</p>
          <p className="text-[12px] text-zinc-100 mt-1.5 leading-snug">
            {n.suggestedPerHr} g/hr would suit this race, but you've sustained {n.practisedPerHr} g/hr
            in training — so that's what we've planned. Build up in long sessions with these exact
            products before you raise it.
          </p>
        </div>
      )}
      {n.underpractised && (
        <div className="border border-[#FF9500]/50 bg-[#FF9500]/[0.07] p-3 mb-3" data-testid="timetable-underpractised">
          <p className="text-[12px] text-zinc-100 leading-snug">
            You've held this rate for {n.practisedHours} h. This race is {fmtMin(n.durMin)} — tolerating
            a rate early says little about hour {Math.floor(n.durMin / 60)}. Rehearse the back end
            {n.sources.food ? ", and expect to lean on real food later on." : " with carb drink and chews, not more gels."}
          </p>
        </div>
      )}

      <p className="text-[12px] text-zinc-300 mb-3">
        A 30 g feed every <b>{interval} minutes</b> = {n.cph} g/hr. Set a {interval}-minute beep and take
        the row — nothing to work out on the day. Your mix: <b>{mix}</b>.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] tabular-nums">
          <thead>
            <tr className="text-[9px] uppercase tracking-widest text-zinc-400 text-left">
              <th className="py-1.5 pr-2">Elapsed</th>
              <th className="py-1.5 pr-2">Clock</th>
              <th className="py-1.5 pr-2">Take</th>
              <th className="py-1.5 pr-2">Carbs</th>
              <th className="py-1.5 pr-2">Fluid</th>
              <th className="py-1.5 pr-2">Sodium</th>
              <th className="py-1.5">Running total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.t} className="border-t border-white/8" data-testid={`timetable-row-${r.t}`}>
                <td className="py-1.5 pr-2 text-zinc-400">{fmtMin(r.t)}</td>
                <td className="py-1.5 pr-2 text-zinc-400">{r.clock}</td>
                <td className="py-1.5 pr-2 font-bold"
                  style={{ color: r.source === "gel" ? "#E2E30A" : r.source === "food" ? "#34C759" : "#22D3EE" }}>
                  {r.item}
                </td>
                <td className="py-1.5 pr-2 text-white font-bold">{r.c}g</td>
                <td className="py-1.5 pr-2 text-zinc-100">{r.f}ml</td>
                <td className="py-1.5 pr-2 text-zinc-100">{r.na}mg</td>
                <td className="py-1.5 text-zinc-400">{r.cumC}g · {r.cumF}ml · {r.cumNa}mg</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-400 mt-3">
        Finish line total: <span className="text-white font-bold">{cumC}g carbs</span> ·{" "}
        {cumF}ml fluid · {cumNa}mg sodium — of which <b>{counts.gel} gel{counts.gel === 1 ? "" : "s"}</b>.
        Fluid is planned for your sweat rate, never raised to carry more carbs.
      </p>
      {unfilled > 0 && (
        <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/[0.07] p-3 mt-3" data-testid="timetable-shortfall">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] font-bold">Carb shortfall</p>
          <p className="text-[12px] text-zinc-100 mt-1.5 leading-snug">
            {unfilled} feed{unfilled === 1 ? "" : "s"} (<b>{unfilled * GEL_CARBS} g carbs</b>) can't be
            filled from what you've said you'll take — your gel limit is reached and the other sources
            are switched off. Either accept carb drink, chews or real food, or raise your gel limit,
            then rehearse it in training. We won't pretend the gap isn't there.
          </p>
        </div>
      )}
      {n.slowerFinishMin > n.durMin && (
        <p className="text-[11px] text-zinc-400 mt-2" data-testid="timetable-slower-finish">
          If it takes you {fmtMin(n.slowerFinishMin)}, carry{" "}
          <b>{Math.ceil(((n.slowerFinishMin - n.durMin) / interval))} extra feed
          {Math.ceil((n.slowerFinishMin - n.durMin) / interval) === 1 ? "" : "s"}</b> — as food or drink
          mix, not more gels.
        </p>
      )}
    </SectionCard>
  );
}

// ------------------------------------------------------ electrolytes/sodium
export function ElectrolytesCard({ n, startTime = "08:00" }) {
  if (!n?.durMin) return null;
  const { cumNa } = feedSchedule(n, startTime);
  const naTotal = cumNa || n.naTotal;
  const tabsNeeded = Math.ceil(naTotal / 300);
  const tabs = Math.min(tabsNeeded, MAX_TABS);
  const sachets = Math.ceil(naTotal / 1000);
  const hotPerHr = Math.min(Math.round(n.naPerHr * 1.3 / 10) * 10, MAX_SODIUM_PER_HR);
  return (
    <SectionCard icon={Beaker} color="#22D3EE" title="Electrolytes & Sodium"
      sub={n.heat ? "Hot conditions" : "Mild conditions"} testid="race-electrolytes">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">Sodium / hour</p>
          <p className="font-display text-2xl text-white leading-none mt-1 tabular-nums">{n.naPerHr}mg</p>
        </div>
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">Race total</p>
          <p className="font-display text-2xl text-white leading-none mt-1 tabular-nums">{naTotal}mg</p>
        </div>
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">If it's hot</p>
          <p className="font-display text-2xl text-white leading-none mt-1 tabular-nums">{hotPerHr}mg<span className="text-[10px] text-zinc-500">/hr</span></p>
        </div>
      </div>

      {(n.naCapped || tabsNeeded > MAX_TABS) && (
        <div className="border border-[#FF9500]/50 bg-[#FF9500]/[0.07] p-3 mb-3" data-testid="electrolyte-cap">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF9500] font-bold">Capped on purpose</p>
          <p className="text-[12px] text-zinc-100 mt-1.5 leading-snug">
            For a {fmtMin(n.durMin)} race we cap sodium at <b>{n.naCeiling} mg/hr</b>
            {n.heat ? " (hot conditions)" : ""} and <b>{MAX_TABS} tabs</b> total.
            {tabsNeeded > MAX_TABS && <> The raw figure works out at {tabsNeeded} × 300 mg tabs — far too many to stomach.</>}{" "}
            Use <b>{sachets} × 1000 mg sachets</b> in your bottles instead, salt your real food, and
            take savoury options (broth, salted potatoes, crisps) at aid stations.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        <Bullet testid="electrolyte-tabs">
          That's roughly <b>{tabs} × 300 mg electrolyte tabs</b> or <b>{sachets} × 1000 mg sachets</b> across the race.
        </Bullet>
        <Bullet>Put sodium <b>in your bottles</b>, not in your mouth dry — it absorbs with fluid, not without it.</Bullet>
        <Bullet>Heavy, salty sweater (white marks on kit)? Take the hot-conditions number even on a cool day.</Bullet>
        <Bullet>Magnesium and potassium matter far less than sodium in-race — don't overthink them.</Bullet>
        <Bullet>
          Too much sodium without fluid causes nausea and a dry, salty mouth. If that hits,
          drink plain water for 20 minutes before taking more.
        </Bullet>
      </ul>
    </SectionCard>
  );
}

// ------------------------------------------------------------ what to carry
// Reads straight off the timetable, so the counts can never disagree with the
// schedule — and nothing is double-counted between gels, drink and food.
export function WhatToCarry({ n, startTime = "08:00" }) {
  if (!n?.durMin) return null;
  const { rows, counts, cumF, cumNa } = feedSchedule(n, startTime);
  if (!rows.length) return null;
  const fills = Math.max(1, Math.ceil(cumF / 500));
  const bottles = Math.min(fills, 2);
  const refills = Math.max(0, fills - bottles);
  const spares = counts.gel ? 1 : 0;
  const tabsNeeded = Math.ceil(cumNa / 300);
  const tabs = Math.min(tabsNeeded, MAX_TABS);
  const tabsCapped = tabsNeeded > MAX_TABS;
  const lines = [
    counts.gel && { k: "gels", v: `${counts.gel + spares} × 30 g gels`, note: spares ? `${counts.gel} on the timetable, ${spares} spare` : "", c: "#E2E30A" },
    counts.drink && { k: "drink", v: `${counts.drink} × 30 g carb drink servings`, note: `in your ${bottles} bottle${bottles > 1 ? "s" : ""}`, c: "#22D3EE" },
    counts.chews && { k: "chews", v: `${counts.chews} × 30 g chew packs`, note: "", c: "#22D3EE" },
    counts.food && { k: "food", v: `${counts.food} real-food portions (~30 g carbs each)`, note: "rice cakes, banana, salted potato, sandwich, flat cola late on", c: "#34C759" },
  ].filter(Boolean);
  return (
    <SectionCard icon={Package} color="#FBBF24" title="What To Carry"
      sub={`${n.carbTotal} g carbs across ${rows.length} feeds`} testid="race-carry">
      <ul className="space-y-2 mb-3">
        {lines.map((l) => (
          <li key={l.k} className="flex items-start gap-2 text-[13px] text-white leading-snug" data-testid={`carry-${l.k}`}>
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0" style={{ background: l.c }} />
            <span><b>{l.v}</b>{l.note && <span className="text-zinc-400"> — {l.note}</span>}</span>
          </li>
        ))}
        <li className="flex items-start gap-2 text-[13px] text-white leading-snug" data-testid="carry-fluid">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-[#22D3EE]" />
          <span>
            <b>{bottles} × 500 ml bottle{bottles > 1 ? "s" : ""}</b>
            {refills > 0 && <span className="text-zinc-400"> — refilled {refills}× on course ({cumF} ml total)</span>}
          </span>
        </li>
      </ul>

      <ul className="space-y-2">
        <Bullet testid="carry-sodium">
          Add <b>{tabs} electrolyte tabs/sachets</b> for the sodium — one per bottle, rest in a pocket.
        </Bullet>
        {tabsCapped && (
          <Bullet testid="carry-tab-cap">
            <b>Capped:</b> the full sodium figure would be {tabsNeeded} × 300 mg tabs, which is
            impractical and hard on the stomach. Use <b>1000 mg sachets in your bottles</b> instead
            (about {Math.ceil(cumNa / 1000)} of them) and <b>salt your real food</b>.
          </Bullet>
        )}
        <Bullet>Count it out the night before and lay it in start order. Pockets get stupid at mile 18.</Bullet>
        <Bullet>Know what's on course: if aid stations have your brand, carry less and top up as you go.</Bullet>
        {n.cph > 60 && <Bullet>Above 60 g/hr you <b>must</b> mix glucose + fructose sources (about 2:1) — one type alone will sit in your gut.</Bullet>}
        <Bullet testid="carry-no-catchup">
          Miss a feed? Take the next one on schedule. Never double up to catch up — that's how races
          end in a hedge.
        </Bullet>
        {n.longRace && n.sources.food && (
          <Bullet testid="carry-long-race">
            Races this long aren't won on gels. The middle hours are deliberately <b>savoury and
            real food</b> — save the sweet stuff for when you need a lift.
          </Bullet>
        )}
      </ul>
    </SectionCard>
  );
}

// ---------------------------------------------------------- sweat/hydration
export function SweatHydrationCard({ n }) {
  if (!n?.durMin) return null;
  return (
    <SectionCard icon={Droplets} color="#22D3EE" title="Sweat & Hydration"
      sub="Drink to a plan, not to thirst" testid="race-hydration">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">Mild conditions</p>
          <p className="font-display text-2xl text-white leading-none mt-1 tabular-nums">500–750<span className="text-[10px] text-zinc-500">ml/hr</span></p>
        </div>
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">Heat / humidity</p>
          <p className="font-display text-2xl text-white leading-none mt-1 tabular-nums">750–1000<span className="text-[10px] text-zinc-500">ml/hr</span></p>
        </div>
      </div>
      <ul className="space-y-2">
        <Bullet testid="hydration-plan">Your plan: <b>{n.fluidPerHr}ml/hr</b> — about {n.fluidTotal}ml over the race.</Bullet>
        <Bullet>
          <b>Find your real sweat rate:</b> weigh yourself naked before and after a 60-minute session in
          similar conditions. Each 1 kg lost = ~1 litre of sweat. Add back anything you drank.
        </Bullet>
        <Bullet>Losing more than <b>2–3% of body weight</b> hurts performance{n.weightKg ? ` — for you that's about ${(n.weightKg * 0.025).toFixed(1)} kg` : ""}.</Bullet>
        <Bullet>
          <b>Don't over-drink.</b> Sloshing stomach, puffy fingers, a headache and needing to pee clear
          mid-race mean you're ahead of your sweat rate — back off and get sodium in.
        </Bullet>
        <Bullet>Pale straw urine the morning of the race = properly hydrated. Clear = too much water, dark = behind.</Bullet>
      </ul>
    </SectionCard>
  );
}

// ------------------------------------------------------------ gut training
export function GutTrainingCard({ n }) {
  if (!n?.durMin) return null;
  return (
    <SectionCard icon={Repeat} color="#A3E635" title="Rehearse It First"
      sub="Train the gut, not just the legs" testid="race-gut-training">
      <ul className="space-y-2">
        <Bullet testid="gut-rehearse">
          Take <b>{n.cph} g carbs/hr</b> in at least <b>2–3 long sessions</b> before race day, using the exact
          products you'll race with. The gut adapts in 2–4 weeks.
        </Bullet>
        <Bullet>Rehearse the race-morning breakfast too, at the same clock time, before a long session.</Bullet>
        <Bullet>Build up: start at 30–40 g/hr, add ~10 g/hr each long session until race pace feels fine.</Bullet>
        <Bullet>If you get GI distress, drop concentration (more fluid per gram), not the carbs.</Bullet>
        <Bullet>Nothing new on race day. Not a new gel, not a new flavour, not a new bottle.</Bullet>
      </ul>
    </SectionCard>
  );
}

// -------------------------------------------------------- race-morning meal
export function MorningMealBlock({ startTime = "08:00", weightKg, durMin = 180 }) {
  if (!weightKg) return null;
  const carbsG = capPreCarbs(weightKg * 1.5, weightKg, durMin);
  const backupCarbsG = Math.max(30, Math.round(carbsG * 0.65));
  return (
    <SectionCard icon={Utensils} color="#FDE68A" title="Race-Morning Meal"
      sub="Timed backwards from the gun" testid="race-morning-meal">
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div className="border border-white/10 bg-black/30 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-[#E2E30A] font-bold">{timeSubtractStr(startTime, 3)} · Main breakfast</p>
          <p className="text-[12px] text-zinc-100 mt-1 leading-snug">
            <span className="text-white font-bold">~{carbsG} g carbs</span> low-fibre, low-fat.
            Oats + banana + honey + a splash of milk is the classic.
          </p>
        </div>
        <div className="border border-white/10 bg-black/30 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-[#E2E30A] font-bold">{timeSubtractStr(startTime, 1)} · Fluid + electrolytes</p>
          <p className="text-[12px] text-zinc-100 mt-1 leading-snug">
            500–750 ml fluid with sodium (400–800 mg). Small carb top-up (banana or gel) OK if hungry.
          </p>
        </div>
      </div>
      <div className="border border-[#FF9500]/40 bg-[#FF9500]/[0.05] px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-wider text-[#FF9500] font-bold">Backup · If guts are unsettled</p>
        <p className="text-[12px] text-zinc-100 mt-1 leading-snug">
          Drop to <span className="text-white font-bold">~{backupCarbsG} g carbs</span> using white toast + jam
          or plain rice cakes. Skip milk, skip fibre, sip electrolytes.
        </p>
      </div>
    </SectionCard>
  );
}

// -------------------------------------------------------------- final top-up
export function TopUpBlock({ startTime = "08:00", weightKg }) {
  if (!weightKg) return null;
  const carbsG = Math.round(weightKg * 0.5);
  const cafLow = Math.round(weightKg * 3);
  const cafHigh = Math.round(weightKg * 6);
  return (
    <SectionCard icon={Timer} color="#FCA5A5" title="Final Top-Up"
      sub={`${timeSubtractStr(startTime, 1)} → gun`} testid="race-topup">
      <ul className="space-y-2">
        <Bullet testid="topup-carbs"><b>~{carbsG} g carbs</b> in the last 60 min — a gel, sports drink, or half a banana.</Bullet>
        <Bullet testid="topup-fluid">Sip <b>250–500 ml</b> fluid + electrolytes; small final swig 15 min before start.</Bullet>
        <Bullet testid="topup-caffeine">
          If you use caffeine: <b>{cafLow}–{cafHigh} mg</b> (3–6 mg/kg) at {timeSubtractStr(startTime, 1)} —
          only if you've trained with it.
        </Bullet>
        <Bullet testid="topup-toilet">Toilet stop 20 min before start. Warm-up jog + strides.</Bullet>
      </ul>
    </SectionCard>
  );
}

// ------------------------------------------------------------ in-race target
export function InRaceBlock({ n, sport = "endurance" }) {
  if (!n?.durMin) return null;
  const totalMin = Math.round((n.recMin * n.durMin) / 60);
  const totalMax = Math.round((n.recMax * n.durMin) / 60);
  return (
    <SectionCard icon={Zap} color="#FB7185" title="In-Race Fuelling Target"
      sub={String(sport).toUpperCase()} testid="race-inrace">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">Per hour</p>
          <p className="font-display text-2xl text-white leading-none mt-1 tabular-nums">{n.cph}g</p>
          <p className="text-[10px] text-zinc-500 mt-1">guideline {n.recMin}–{n.recMax} g/hr</p>
        </div>
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">Race total</p>
          <p className="font-display text-2xl text-white leading-none mt-1 tabular-nums">{n.carbTotal}g</p>
          <p className="text-[10px] text-zinc-500 mt-1">range {totalMin}–{totalMax}g over {fmtMin(n.durMin)}</p>
        </div>
      </div>
      <ul className="space-y-2">
        <Bullet>Alternate glucose + fructose (2:1) once you push above 60 g/hr to avoid GI issues.</Bullet>
        <Bullet>Fluid: {n.fluidPerHr}ml/hr for your conditions. Sip early — don't wait until you're thirsty.</Bullet>
        <Bullet>Set a beep every {n.feedInterval || 20} min so you don't forget to fuel on autopilot.</Bullet>
        {n.cphCapped && n.requested > 0 && (
          <Bullet testid="inrace-carb-cap">
            <b>Capped at {n.ceiling} g/hr for a {fmtMin(n.durMin)} race.</b> You asked for{" "}
            {n.requested} g/hr — over this distance you simply don't need it, and your gut won't
            thank you for it.
          </Bullet>
        )}
        {n.shortRace && (
          <Bullet testid="inrace-short-race">
            Under 75 minutes you need very little on board — a sports drink, or even just a
            carb mouth-rinse, is enough. Fuel the days before instead.
          </Bullet>
        )}
        {n.longRace && (
          <Bullet testid="inrace-long-race">
            Over {Math.round(n.durMin / 60)} hours, tolerance beats maximum. Aim{" "}
            <b>{n.recMin}–{n.recMax} g/hr</b> from mixed sources{n.sources.food ? " including real food, and expect to want savoury by halfway." : " — gels, chews and carb drink rotated, so you're never on one flavour for hours."}
          </Bullet>
        )}
      </ul>
    </SectionCard>
  );
}

// ----------------------------------------------------------- post-race block
export function PostRaceBlock({ weightKg }) {
  if (!weightKg) return null;
  const carbsG = Math.round(weightKg * 1.0);
  const proteinLow = Math.round(weightKg * 0.3);
  const proteinHigh = Math.round(weightKg * 0.4);
  return (
    <SectionCard icon={HeartPulse} color="#E2E30A" title="Post-Race Recovery"
      sub="First 60 min after the line" testid="race-postrace">
      <ul className="space-y-2">
        <Bullet testid="postrace-carbs"><b>~{carbsG} g carbs</b> within 60 min — smoothie, rice + chicken, chocolate milk all work.</Bullet>
        <Bullet testid="postrace-protein"><b>{proteinLow}–{proteinHigh} g protein</b> alongside carbs for muscle repair.</Bullet>
        <Bullet testid="postrace-rehydrate">Rehydrate to <b>150 % of body-weight lost</b> in the first 4 h with electrolytes.</Bullet>
        <Bullet>Avoid alcohol until you've eaten, weighed in and re-hydrated.</Bullet>
        <Bullet>Sleep is the biggest recovery tool. Bank an early night.</Bullet>
      </ul>
    </SectionCard>
  );
}

// -------------------------------------------------------- race-week timeline
const TAPER = [
  { key: 3, title: "T-3 · Start of the load", color: "#FDE68A" },
  { key: 2, title: "T-2 · Load builds", color: "#FEF08A" },
  { key: 1, title: "T-1 · Peak load", color: "#FCA5A5" },
  { key: 0, title: "T-0 · Race Day", color: "#FF3B30" },
  { key: -1, title: "T+1 · Recovery", color: "#E2E30A" },
];
const TAPER_TIPS = {
  3: ["Carbs +1 g/kg — start easing them up.", "Hydration normal. No extra electrolytes yet.", "Last quality session okay. From here — taper.", "Skip alcohol from tonight."],
  2: ["Carbs +2 g/kg. Skew meals carb-heavy.", "Add one electrolyte serving with a meal.", "Ease off high-fibre veg / bran / lentils.", "Bank sleep tonight — 2-nights-out is the money sleep."],
  1: ["Peak load: +3 g/kg carbs. Easy carbs win — rice, pasta, oats, sports drinks.", "YES electrolytes today. 500–800 mg sodium with meals. Pee pale by evening.", "Zero alcohol. Zero.", "Kit out tonight. Set two alarms. Bed early even if you can't sleep."],
  0: ["Race breakfast 2.5–3 h before the gun.", "Fluid + electrolytes 90 min before, top up 15 min before.", "Follow the fuelling timetable from the gun.", "Post: 1 g/kg carbs + 20–40 g protein within 60 min."],
  "-1": ["Rehydrate fully — 150 % of body-weight lost.", "High protein day: 2.0–2.4 g/kg.", "Carbs stay ELEVATED — do not trim (glycogen refill).", "Light walk. No structured training."],
};

export function TimelineBlock({ raceIso, weightKg }) {
  return (
    <SectionCard icon={Flag} color="#E2E30A" title="Race-Week Timeline" sub="T-3 → T+1" testid="race-timeline">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {TAPER.map((t) => {
          const dayIso = addDaysISO(raceIso, -t.key);
          const label = parseLocal(dayIso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
          const bump = t.key === 1 ? 3 : t.key === 2 ? 2 : t.key === 3 ? 1 : 0;
          const carbsG = weightKg && bump ? Math.round(weightKg * (5 + bump)) : null;
          return (
            <div key={t.key} className="border border-white/10 bg-black/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: t.color }}>{t.title}</p>
                <p className="text-[10px] font-mono text-zinc-500 uppercase">{label}</p>
              </div>
              {carbsG && <p className="text-xs text-white mb-2"><b>~{carbsG} g</b> carbs target</p>}
              <ul className="space-y-1.5">
                {(TAPER_TIPS[t.key] || TAPER_TIPS["-1"]).map((tip, i) => (
                  <li key={i} className="flex gap-1.5 items-start text-[11px] leading-snug text-zinc-200">
                    <span className="mt-1 h-1 w-1 bg-white/50 shrink-0" /><span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ------------------------------------------------------------- kit checklist
export const DEFAULT_KIT = [
  "Race number + timing chip attached",
  "Race kit laid out night before",
  "Pre-race breakfast prepped (or purchased at race hotel)",
  "Bottles pre-mixed (electrolytes + carbs)",
  "Gels packed (2 more than you need)",
  "Sunscreen / anti-chafe / lube",
  "Warm-up layers for start line",
  "Post-race change of clothes + recovery meal packed",
  "Charged watch + head unit + heart-rate strap",
  "Backup pins, tools, spare tube (bike)",
];

export function ChecklistBlock({ raceIso }) {
  const storageKey = `spc-race-checklist-${raceIso || "default"}`;
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch { return {}; }
  });
  const toggle = (i) => {
    setChecked((c) => {
      const next = { ...c, [i]: !c[i] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  };
  const done = Object.values(checked).filter(Boolean).length;
  return (
    <SectionCard icon={CheckCircle2} color="#34C759" title="Kit & Fuelling Checklist"
      sub={`${done} / ${DEFAULT_KIT.length} ready`} testid="race-checklist">
      <ul className="grid sm:grid-cols-2 gap-1.5">
        {DEFAULT_KIT.map((item, i) => (
          <li key={i}>
            <button type="button" onClick={() => toggle(i)} data-testid={`race-checklist-item-${i}`}
              className={`w-full flex items-start gap-2 text-left px-2 py-1.5 border border-white/10 hover:border-white/25 transition-colors text-[12px] ${checked[i] ? "text-zinc-500 line-through" : "text-zinc-100"}`}>
              {checked[i]
                ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#34C759]" />
                : <Circle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-zinc-500" />}
              <span>{item}</span>
            </button>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

// ------------------------------------------- structured export (PDF / print)
// One structured description of the plan, rendered by both the PDF builder and
// the print view so every export says exactly the same thing.
export function racePlanSections({
  name, raceIso, startTime = "08:00", n, weightKg, dailyTargets = [],
  fuelling = [], raceIdx = -1, raceSession, strategy, brief, sport = "endurance",
}) {
  const sections = [];
  const pre = capPreCarbs(weightKg * 1.5, weightKg, n.durMin);

  // Race day at a glance — mirrors the headline card on screen.
  const glance = [
    `Before: ${capPreCarbs(raceSession?.preG ?? weightKg * 2, weightKg, n.durMin)} g carbs · ${Math.min(raceSession?.preFluidMl ?? 500, 750)} ml fluid`,
    `During (per hour): ${n.cph} g carbs · ${n.fluidPerHr} ml fluid · ${n.naPerHr} mg sodium — ${n.carbTotal} g carbs total`,
    `After: ${capPostCarbs(raceSession?.postG ?? weightKg, weightKg)} g carbs · ${Math.min(raceSession?.postFluidMl ?? 750, 1000)} ml fluid`,
    `Hydration: ${n.fluidPerHr} ml/hr with ~${n.naPerHr} mg sodium/hr${n.heat ? " (hot conditions)" : ""} — capped to what's sensible for a ${fmtMin(n.durMin)} race.`,
  ];
  if (raceSession?.note) glance.push(raceSession.note);
  if (strategy) glance.push(strategy);
  sections.push({ heading: `Race day at a glance — ${sport}`, lines: glance });

  // Smart race brief (only when one has been generated)
  if (brief && (brief.raceMorning || brief.headline)) {
    const bl = [];
    if (brief.headline) bl.push(brief.headline);
    const clean = (s) => String(s).replace(/\*\*/g, "");
    if (brief.theNightBefore) bl.push(`The night before: ${clean(brief.theNightBefore)}`);
    if (brief.raceMorning) bl.push(`Race morning: ${clean(brief.raceMorning)}`);
    if (brief.duringTheRace) bl.push(`During the race: ${clean(brief.duringTheRace)}`);
    if (brief.afterwards) bl.push(`Afterwards: ${clean(brief.afterwards)}`);
    (brief.carry || []).forEach((c) => bl.push(`Carry: ${clean(c)}`));
    (brief.contingency || []).forEach((c) => bl.push(`If it goes wrong: ${clean(c)}`));
    sections.push({ heading: "Smart race brief", lines: bl });
  }

  sections.push({
    heading: "Race morning",
    lines: [
      `${timeSubtractStr(startTime, 3)} — Breakfast: ~${pre} g carbs, low fibre, low fat`,
      `${timeSubtractStr(startTime, 1)} — 500-750 ml fluid with 400-800 mg sodium`,
      `${timeSubtractStr(startTime, 1)} — Caffeine (optional): ${Math.round(weightKg * 3)}-${Math.round(weightKg * 6)} mg, only if trained with it`,
      `${timeSubtractStr(startTime, 0.25)} — Final ~${Math.round(weightKg * 0.5)} g carbs and a small swig of fluid`,
      "Toilet stop 20 min before the gun. Warm-up jog and strides.",
    ],
  });

  // Schedule first — the in-race and carry sections quote its totals.
  const fs = feedSchedule(n, startTime);
  const rows = fs.rows.map((r) => [
    fmtMin(r.t), r.clock, r.item, `${r.f} ml`, `${r.na} mg`, `${r.cumC} g / ${r.cumF} ml`,
  ]);
  const cc = fs.cumC, cf = fs.cumF, cn = fs.cumNa;

  const inRace = [
    `Carbs: ${n.cph} g/hr (guideline ${n.recMin}-${n.recMax} g/hr for ${fmtMin(n.durMin)})`,
    `Fluid: ${n.fluidPerHr} ml/hr${n.heat ? " (hot conditions)" : ""}`,
    `Sodium: ${n.naPerHr} mg/hr`,
    `Race totals: ${cc} g carbs, ${cf} ml fluid, ${cn} mg sodium`,
  ];
  if (n.cphCapped && n.requested > 0) inRace.push(`Capped at ${n.ceiling} g/hr — you asked for ${n.requested} g/hr, which this distance does not need.`);
  if (n.shortRace) inRace.push("Under 75 minutes a sports drink or carb mouth-rinse is enough.");
  if (n.cph > 60) inRace.push("Mix glucose and fructose sources (2:1) at this rate.");
  if (n.longRace && n.sources.food) inRace.push("Plan savoury and real food for the middle hours.");
  sections.push({ heading: "In-race targets", lines: inRace });

  // Feed-event timetable (30 g per feed, sources rotated)
  if (rows.length) {
    const mix = Object.entries(fs.counts || {}).filter(([, v]) => v > 0)
      .map(([k, v]) => `${v} x ${FUEL_SOURCES[k].label}`).join(", ");
    const lines = [
      `${n.cph} g carbs/hr = one 30 g feed every ${fs.interval} minutes. Set a ${fs.interval}-minute beep.`,
      `Suggested ${n.suggestedPerHr} g/hr · practised ${n.practisedPerHr || "not recorded"} g/hr · planned ${n.cph} g/hr.`,
      `Your mix: ${mix}.`,
    ];
    if (n.provisional) {
      lines.push("PROVISIONAL: practised intake and gel limits not recorded — planned conservatively at no more than "
        + `${n.gelsPerHrLimit} gels an hour. Add them in race setup for a race-ready plan.`);
    }
    if (n.unpractised) {
      lines.push(`Capped to practised tolerance: ${n.suggestedPerHr} g/hr would suit this race, but ${n.practisedPerHr} g/hr is what you've sustained.`);
    }
    sections.push({
      heading: `Fuelling timetable — a 30 g feed every ${fs.interval} min (${fmtMin(n.durMin)})`,
      lines,
      table: { head: ["Elapsed", "Clock", "Take", "Fluid", "Sodium", "Running total"], rows },
      linesAfterTable: [`Finish line: ${cc} g carbs, ${cf} ml fluid, ${cn} mg sodium — of which ${fs.counts.gel || 0} gels. Fluid is planned for your sweat rate, never raised to carry more carbs.`],
    });
  }

  // PDF fluid/sodium come from the same schedule rows as the on-screen cards.
  const fills = Math.max(1, Math.ceil(cf / 500));
  const bottles = Math.min(fills, 2);
  const refills = Math.max(0, fills - bottles);
  const c = fs.counts || {};
  const tabsNeeded = Math.ceil(cn / 300);
  const tabs = Math.min(tabsNeeded, MAX_TABS);
  const carry = [];
  if (c.gel) carry.push(`${c.gel + 1} gels at 30 g each (${c.gel} on the timetable, 1 spare)`);
  if (c.drink) carry.push(`${c.drink} x 30 g carb drink servings, mixed in your bottles`);
  if (c.chews) carry.push(`${c.chews} x 30 g chew packs`);
  if (c.food) carry.push(`${c.food} real-food portions at ~30 g carbs each — rice cakes, banana, salted potato, sandwich, flat cola late on`);
  carry.push(`${bottles} x 500 ml bottle${bottles > 1 ? "s" : ""}${refills ? `, refilled ${refills}x on course` : ""} (${cf} ml total)`);
  carry.push(`${tabs} electrolyte tabs (300 mg) or ${Math.ceil(cn / 1000)} x 1000 mg sachets`);
  if (n.cph > 60) carry.push("Above 60 g/hr, mix glucose and fructose sources about 2:1.");
  carry.push("Miss a feed? Take the next one on schedule — never double up to catch up.");
  if (tabsNeeded > MAX_TABS) {
    carry.push(`Capped: ${tabsNeeded} tabs is impractical. Use 1000 mg sachets in bottles and salt your real food.`);
  }
  if (n.slowerFinishMin > n.durMin) {
    carry.push(`If it takes ${fmtMin(n.slowerFinishMin)}, carry ${Math.ceil((n.slowerFinishMin - n.durMin) / fs.interval)} extra feeds — as food or drink mix, not more gels.`);
  }
  carry.push("Count it out the night before and lay it in start order.");
  sections.push({ heading: "What to carry", lines: carry });

  // Electrolytes & sodium detail
  const eHot = Math.min(Math.round(n.naPerHr * 1.3 / 10) * 10, MAX_SODIUM_PER_HR);
  const eSachets = Math.ceil(cn / 1000);
  const elec = [
    `Sodium: ${n.naPerHr} mg/hr · ${cn} mg race total · if it's hot, ${eHot} mg/hr.`,
    `Roughly ${tabs} x 300 mg electrolyte tabs or ${eSachets} x 1000 mg sachets across the race.`,
    "Put sodium in your bottles, not dry in your mouth — it absorbs with fluid, not without it.",
    "Heavy, salty sweater (white marks on kit)? Take the hot-conditions number even on a cool day.",
    "Magnesium and potassium matter far less than sodium in-race — don't overthink them.",
    "Too much sodium without fluid causes nausea and a dry, salty mouth. If that hits, drink plain water for 20 minutes before taking more.",
  ];
  if (n.naCapped || tabsNeeded > MAX_TABS) {
    elec.unshift(`Capped on purpose: for a ${fmtMin(n.durMin)} race we cap sodium at ${n.naCeiling} mg/hr${n.heat ? " (hot conditions)" : ""} and ${MAX_TABS} tabs total. Use ${eSachets} x 1000 mg sachets in your bottles and salt your real food.`);
  }
  sections.push({ heading: "Electrolytes & sodium", lines: elec });

  sections.push({
    heading: "Sweat & hydration",
    lines: [
      "Mild conditions: 500-750 ml/hr. Heat and humidity: 750-1000 ml/hr.",
      `Plan: ${n.fluidPerHr} ml/hr — about ${n.fluidTotal} ml over the race.`,
      "Find your sweat rate: weigh naked before and after a 60-min session in similar conditions. 1 kg lost = ~1 litre of sweat.",
      `Losing more than 2-3% of body weight hurts performance${weightKg ? ` — about ${(weightKg * 0.025).toFixed(1)} kg for you` : ""}.`,
      "Sloshing stomach, puffy fingers or clear urine mid-race means you are ahead of your sweat rate.",
      "Pale straw urine on race morning = properly hydrated. Clear = too much water, dark = behind.",
    ],
  });

  sections.push({
    heading: "Rehearse it first — train the gut",
    lines: [
      `Take ${n.cph} g carbs/hr in at least 2-3 long sessions before race day, using the exact products you'll race with. The gut adapts in 2-4 weeks.`,
      "Rehearse the race-morning breakfast too, at the same clock time, before a long session.",
      "Build up: start at 30-40 g/hr, add ~10 g/hr each long session until race pace feels fine.",
      "If you get GI distress, drop concentration (more fluid per gram), not the carbs.",
      "Nothing new on race day. Not a new gel, not a new flavour, not a new bottle.",
    ],
  });

  sections.push({
    heading: "Post-race (first 60 min)",
    lines: [
      `~${capPostCarbs(weightKg * 1.0, weightKg)} g carbs and ${Math.round(weightKg * 0.3)}-${Math.round(weightKg * 0.4)} g protein`,
      "Rehydrate to 150% of body weight lost, with electrolytes",
      "No alcohol until you have eaten, weighed in and rehydrated",
    ],
  });

  if (dailyTargets.length) {
    sections.push({
      heading: "Race week daily targets",
      table: {
        head: ["Day", "kcal", "Carbs", "Protein", "Fat", "Fluid"],
        rows: dailyTargets.map((d, i) => [
          DAY_NAMES[i] || `Day ${i + 1}`, String(d.energy), `${d.carb} g`,
          `${d.protein} g`, `${d.fat} g`, `${d.hydrationMl} ml`,
        ]),
      },
    });
  }

  // Per-session fuelling for the rest of race week
  const sessLines = [];
  fuelling.forEach((f, i) => {
    if (i === raceIdx) return;
    (f?.sessions || []).forEach((s) => {
      sessLines.push(`${DAY_NAMES[i] || `Day ${i + 1}`} — ${s.sport} · ${s.duration} min: ${s.preG} g before, ${s.duringPerHr} g/hr during, ${s.postG} g after`);
    });
  });
  if (sessLines.length) sections.push({ heading: "Race week session fuelling", lines: sessLines });

  sections.push({
    heading: "Race week timeline",
    lines: TAPER.map((t) => {
      const dayIso = addDaysISO(raceIso, -t.key);
      const bump = t.key === 1 ? 3 : t.key === 2 ? 2 : t.key === 3 ? 1 : 0;
      const carbsG = weightKg && bump ? ` (~${Math.round(weightKg * (5 + bump))} g carbs)` : "";
      return `${t.title} — ${dayIso}${carbsG}: ${(TAPER_TIPS[t.key] || TAPER_TIPS["-1"]).join(" ")}`;
    }),
  });

  sections.push({ heading: "Kit & fuelling checklist", lines: DEFAULT_KIT.map((k) => `[ ] ${k}`) });

  return {
    title: name || "Race Fuelling Plan",
    subtitle: `Race day ${raceIso} · start ${startTime} · est ${fmtMin(n.durMin)}${weightKg ? ` · ${weightKg} kg` : ""}${n.heat ? " · hot conditions" : ""}`,
    kpis: [
      { label: "Carbs / hr", value: `${n.cph} g`, sub: `${cc} g race total` },
      { label: "Fluid / hr", value: `${n.fluidPerHr} ml`, sub: `${cf} ml race total` },
      { label: "Sodium / hr", value: `${n.naPerHr} mg`, sub: `${cn} mg race total` },
      { label: "Feed every", value: `${fs.interval} min`, sub: `${fs.counts?.gel || 0} gels planned` },
    ],
    sections,
    footer: "SPC Performance Nutrition · guidance only, not medical advice",
  };
}
export function racePlanText({ name, raceIso, startTime, n, weightKg, dailyTargets = [], brief }) {
  const L = [];
  L.push(`RACE FUELLING PLAN — ${name || "Race"}`);
  L.push(`Race day: ${raceIso} · start ${startTime || "08:00"} · est ${fmtMin(n.durMin)}`);
  if (weightKg) L.push(`Athlete weight: ${weightKg} kg`);
  if (brief && (brief.raceMorning || brief.headline)) {
    const clean = (s) => String(s || "").replace(/\*\*/g, "");
    L.push("");
    L.push("SMART RACE BRIEF");
    if (brief.headline) L.push(`  ${clean(brief.headline)}`);
    if (brief.theNightBefore) L.push(`  Night before: ${clean(brief.theNightBefore)}`);
    if (brief.raceMorning) L.push(`  Race morning: ${clean(brief.raceMorning)}`);
    if (brief.duringTheRace) L.push(`  During: ${clean(brief.duringTheRace)}`);
    if (brief.afterwards) L.push(`  Afterwards: ${clean(brief.afterwards)}`);
    (brief.carry || []).forEach((c) => L.push(`  Carry: ${clean(c)}`));
    (brief.contingency || []).forEach((c) => L.push(`  If it goes wrong: ${clean(c)}`));
  }
  L.push("");
  L.push("RACE MORNING");
  L.push(`${timeSubtractStr(startTime, 3)}  Breakfast — ~${capPreCarbs(weightKg * 1.5, weightKg, n.durMin)} g carbs, low fibre/low fat`);
  L.push(`${timeSubtractStr(startTime, 1)}  500-750 ml fluid + 400-800 mg sodium`);
  L.push(`${timeSubtractStr(startTime, 1)}  Caffeine (optional) ${Math.round(weightKg * 3)}-${Math.round(weightKg * 6)} mg`);
  L.push(`${timeSubtractStr(startTime, 0.25)}  Final ~${Math.round(weightKg * 0.5)} g carbs + small swig of fluid`);
  L.push("");
  L.push(`IN RACE — ${n.cph} g carbs/hr · ${n.fluidPerHr} ml/hr · ${n.naPerHr} mg sodium/hr`);
  if (n.cphCapped) L.push(`  (capped at ${n.ceiling} g/hr — appropriate for a ${fmtMin(n.durMin)} race)`);
  if (n.naCapped) L.push(`  (sodium capped at ${n.naCeiling} mg/hr)`);
  let cc = 0, cf = 0;
  const fsT = feedSchedule(n, startTime);
  fsT.rows.forEach((r) => {
    cc = r.cumC; cf = r.cumF;
    L.push(`  +${fmtMin(r.t)} (${r.clock})  ${r.item} · ${r.f} ml · cumulative ${r.cumC} g`);
  });
  L.push(`  TOTAL: ${cc} g carbs · ${cf} ml fluid · ${n.naTotal} mg sodium`);
  L.push("");
  L.push("POST RACE (first 60 min)");
  L.push(`  ~${capPostCarbs(weightKg * 1.0, weightKg)} g carbs + ${Math.round(weightKg * 0.3)}-${Math.round(weightKg * 0.4)} g protein`);
  L.push("  Rehydrate to 150% of body weight lost, with electrolytes");
  if (dailyTargets.length) {
    L.push("");
    L.push("RACE WEEK DAILY TARGETS");
    dailyTargets.forEach((d, i) => {
      L.push(`  ${DAY_NAMES[i] || `Day ${i + 1}`}: ${d.energy} kcal · ${d.carb} g C · ${d.protein} g P · ${d.fat} g F · ${d.hydrationMl} ml`);
    });
  }
  L.push("");
  L.push("Built with SPC Performance Nutrition.");
  return L.join("\n");
}
